import { useState } from 'react';
import { hotTopics, deepDives } from '@/mocks/chigua';
import { colorChip, iconMap } from './icons';

export function SectionHead({
  eyebrow,
  title,
  desc,
}: {
  eyebrow: string;
  title: string;
  desc?: string;
}) {
  return (
    <div className="mb-8 md:mb-10">
      <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
        <span className="h-px w-6 bg-primary-400"></span>
        {eyebrow}
      </p>
      <h2 className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
        {title}
      </h2>
      {desc && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">{desc}</p>}
    </div>
  );
}

const tagFilters = ['全部', '综艺现场', '影视吃瓜', '饭圈疑云', '行业观察', '细节扒皮'];

export function HotRanking() {
  const [active, setActive] = useState('全部');
  const list = active === '全部' ? hotTopics : hotTopics.filter((t) => t.tag === active);

  return (
    <section id="hot" className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <SectionHead
          eyebrow="Hot List"
          title="今日热瓜榜"
          desc="编辑部按讨论热度与线索完整度综合排序，点击分类可只看某一瓜田的动向。"
        />
        <div className="scrollbar-none flex max-w-full gap-1.5 overflow-x-auto rounded-full bg-background-100 p-1">
          {tagFilters.map((t) => (
            <button
              key={t}
              onClick={() => setActive(t)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === t
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:bg-background-50 hover:text-primary-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        {list.map((item, i) => (
          <article
            key={item.title}
            className={`group relative flex gap-4 rounded-xl border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-200 md:p-5 ${
              i === 0 && active === '全部' ? 'border-primary-200 bg-primary-50/60' : ''
            }`}
          >
            <div className="relative shrink-0">
              <span
                className={`font-heading text-4xl font-black leading-none md:text-5xl ${
                  i === 0 && active === '全部' ? 'text-primary-500' : 'text-background-400'
                }`}
              >
                {String(item.rank).padStart(2, '0')}
              </span>
              <i
                className={`mt-2 block text-xl ${iconMap[item.trend]} ${
                  item.trend === 'up' ? 'text-accent-600' : item.trend === 'down' ? 'text-primary-500' : 'text-foreground-400'
                }`}
              ></i>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${colorChip[item.color]}`}>
                  {item.tag}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {item.views}
                </span>
              </div>
              <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                <a href="#dive" className="cursor-pointer hover:text-primary-600">
                  {item.title}
                </a>
              </h3>
              <p className="mt-2 hidden text-sm leading-relaxed text-foreground-600 sm:block">{item.summary}</p>
              <a
                href="#clue"
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 opacity-0 transition-opacity group-hover:opacity-100"
              >
                进入时间线 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-center gap-2 text-sm text-foreground-500">
        <i className="ri-information-line text-primary-500"></i>
        榜单每小时由编辑室复核一次热度，仅整理公开可查信息。
      </div>
    </section>
  );
}

export function DeepArchive() {
  const [query, setQuery] = useState('');
  const list = deepDives.filter((d) =>
    [d.title, d.category, d.excerpt].some((t) => t.includes(query.trim()))
  );

  return (
    <section id="dive" className="border-y border-background-200 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHead
            eyebrow="Deep Dive"
            title="深挖档案"
            desc="每一份档案都是把散落的截图、路透与采访重新排到时间轴上后的完整复盘。"
          />
          <label className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 md:w-80">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索档案标题、栏目或关键词…"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            {query && (
              <button onClick={() => setQuery('')} className="cursor-pointer text-foreground-400 hover:text-primary-600" aria-label="清空">
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </label>
        </div>

        {list.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center text-foreground-500">
            <i className="ri-emotion-sad-line text-4xl"></i>
            <p className="mt-3 text-sm">没有找到相关档案，换个关键词再试试吧。</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((d) => (
              <article
                key={d.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-transform hover:-translate-y-1"
              >
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img
                    src={d.image}
                    alt={d.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">
                    {d.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-400">
                    <span className="font-semibold text-foreground-600">{d.author}</span>
                    <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                    <time>{d.date}</time>
                    <span className="ml-auto whitespace-nowrap">
                      <i className="ri-time-line mr-0.5"></i>
                      {d.readTime}
                    </span>
                  </div>
                  <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
                    <a href="#clue" className="cursor-pointer hover:text-primary-600">
                      {d.title}
                    </a>
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{d.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                    展开复盘 <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}